import React, { useState, useEffect } from 'react';
import { Download, Eye, Loader, AlertCircle, CheckCircle2, Clock, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import apiService from '@/services/api';

interface Invoice {
  id: string;
  invoice_number: string;
  booking_id?: string;
  total_pence: number;
  amount_pence?: number;
  vat_amount_pence?: number;
  payment_status: string;
  paid_amount_pence?: number;
  created_at: string;
  issued_date?: string;
}

interface InvoiceViewProps {
  compact?: boolean;
  onInvoiceView?: () => void;
}

const InvoiceSection: React.FC<InvoiceViewProps> = ({ compact = false, onInvoiceView }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/customer/invoices', 'GET');
      if (response.success && response.data) {
        setInvoices(response.data);
      }
    } catch (err) {
      console.error('Failed to load invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      setDownloading(invoice.id);
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated. Please login first.');
        setDownloading(null);
        return;
      }

      const downloadUrl = `/api/customer/invoices/${invoice.id}/download`;
      
      // Fetch with auth header
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Download failed with status ${response.status}`);
        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (response.status === 404) {
          setError('Invoice not found');
        } else {
          setError(`Download failed: ${response.statusText}`);
        }
        setDownloading(null);
        return;
      }

      // Convert response to blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Downloaded invoice: ${invoice.invoice_number}`);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download invoice');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadReceipt = async (invoice: Invoice) => {
    try {
      setDownloading(invoice.id);
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated. Please login first.');
        setDownloading(null);
        return;
      }

      const downloadUrl = `/api/customer/invoices/${invoice.id}/receipt`;
      
      // Fetch with auth header
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Download failed with status ${response.status}`);
        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (response.status === 404) {
          setError('Receipt not found or payment not completed');
        } else {
          setError(`Download failed: ${response.statusText}`);
        }
        setDownloading(null);
        return;
      }

      // Convert response to blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RECEIPT-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Downloaded receipt: ${invoice.invoice_number}`);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download receipt');
    } finally {
      setDownloading(null);
    }
  };

  // Status labels - These MUST come from API config, not hardcoded here
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: any; icon: any }> = {
      paid: { label: 'Paid', variant: 'default', icon: CheckCircle2 },
      partial: { label: 'Partially Paid', variant: 'outline', icon: Clock },
      pending: { label: 'Pending', variant: 'secondary', icon: Clock },
      unpaid: { label: 'Unpaid', variant: 'destructive', icon: AlertCircle },
      overdue: { label: 'Overdue', variant: 'destructive', icon: AlertCircle },
    };

    const config = statusMap[status.toLowerCase()] || statusMap.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Get section title and description from props or config
  const getSectionTitle = () => onInvoiceView ? 'Recent Invoices' : 'Invoices & Receipts';
  const getSectionDescription = () => onInvoiceView ? 'Your recent invoices' : 'Download your VAT invoices and payment receipts';

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invoices...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!compact && (
        <div>
          <h2 className="text-2xl font-bold">{getSectionTitle()}</h2>
          <p className="text-muted-foreground mt-1">
            {getSectionDescription()}
          </p>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-100 border border-red-400 rounded p-4 flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Eye className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No invoices yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Invoices will appear here once you complete a booking payment
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {invoices.map((invoice) => {
            const totalGBP = (invoice.total_pence / 100).toFixed(2);
            const baseGBP = invoice.amount_pence ? (invoice.amount_pence / 100).toFixed(2) : totalGBP;
            const vatGBP = invoice.vat_amount_pence ? (invoice.vat_amount_pence / 100).toFixed(2) : '0.00';
            const paidGBP = invoice.paid_amount_pence ? (invoice.paid_amount_pence / 100).toFixed(2) : '0.00';
            const outstanding = ((invoice.total_pence - (invoice.paid_amount_pence || 0)) / 100).toFixed(2);

            return (
              <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                    {/* Invoice Info */}
                    <div className="md:col-span-2 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="font-semibold">{invoice.invoice_number}</h3>
                          <p className="text-xs text-muted-foreground">
                            {new Date(invoice.created_at || invoice.issued_date || new Date()).toLocaleDateString(
                              'en-GB',
                              { year: 'numeric', month: 'long', day: 'numeric' }
                            )}
                          </p>
                        </div>
                        {getStatusBadge(invoice.payment_status)}
                      </div>

                      {invoice.booking_id && (
                        <p className="text-xs text-muted-foreground">
                          Booking: #{invoice.booking_id}
                        </p>
                      )}
                    </div>

                    {/* Amount Info */}
                    <div className="md:col-span-1 space-y-1 text-sm">
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium">£{baseGBP}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">VAT (20%):</span>
                        <span className="font-medium">£{vatGBP}</span>
                      </div>
                      <div className="flex justify-between gap-4 border-t pt-1 mt-1">
                        <span className="font-semibold">Total:</span>
                        <span className="font-bold">£{totalGBP}</span>
                      </div>

                      {/* Payment Info */}
                      {invoice.paid_amount_pence !== undefined && (
                        <>
                          <div className="flex justify-between gap-4 text-xs mt-2">
                            <span className="text-muted-foreground">Paid:</span>
                            <span>£{paidGBP}</span>
                          </div>
                          {parseFloat(outstanding) > 0 && (
                            <div className="flex justify-between gap-4 text-xs">
                              <span className="text-red-600">Outstanding:</span>
                              <span className="text-red-600 font-medium">£{outstanding}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="md:col-span-1 flex gap-2 flex-wrap md:flex-col">
                      {/* Pay Now Button for Unpaid Invoices */}
                      {invoice.payment_status !== 'paid' && invoice.payment_status?.toLowerCase() !== 'completed' && (
                        <Button
                          onClick={() => {
                            // Navigate to payment - would need quote ID or invoice ID
                            console.log('Pay Now clicked for invoice:', invoice.id);
                            // TODO: Integrate with Stripe checkout
                          }}
                          size="sm"
                          className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
                          title="Pay this invoice now"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Pay Now - £{totalGBP}
                        </Button>
                      )}

                      <Button
                        onClick={() => handleDownloadInvoice(invoice)}
                        disabled={downloading === invoice.id}
                        size="sm"
                        className="flex-1"
                        title="Download VAT invoice as PDF"
                      >
                        {downloading === invoice.id ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin mr-2" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Invoice
                          </>
                        )}
                      </Button>

                      {invoice.payment_status === 'paid' || invoice.payment_status === 'partial' ? (
                        <Button
                          onClick={() => handleDownloadReceipt(invoice)}
                          disabled={downloading === `receipt-${invoice.id}`}
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          title="Download payment receipt as PDF"
                        >
                          {downloading === `receipt-${invoice.id}` ? (
                            <>
                              <Loader className="h-4 w-4 animate-spin mr-2" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Receipt
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {invoices.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-sm mb-3">Invoice Status Legend:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="flex items-center gap-2">
                <Badge>
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Paid
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  <Clock className="h-3 w-3 mr-1" />
                  Partially Paid
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  <Clock className="h-3 w-3 mr-1" />
                  Pending
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="destructive">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InvoiceSection;
